import { Globe } from 'lucide-react';
import { toast } from 'sonner';
import { useUpdateUser } from '@/core/api';
import { useAuth } from '@/core/auth';
import { useIntl } from '@/core/i18n/use-intl';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/shared/components/ui';

type Language = 'de-DE' | 'en-US';

export function LanguageSwitcher() {
  const { t } = useIntl();
  const { user, updateUser } = useAuth();
  const updateUserMutation = useUpdateUser();

  if (!user) return null;

  const currentLanguage = user.language || 'de-DE';

  const handleLanguageChange = async (language: Language) => {
    try {
      const newUser = await updateUserMutation.mutateAsync({
        id: user.id,
        data: { language },
      });
      updateUser(newUser);
    } catch (_error) {
      toast.error(t('errors.unknownError'));
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="w-full flex items-center gap-3 px-2 py-2 rounded-lg text-sm font-medium text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
        >
          <Globe className="size-[18px]" />
          <span className="flex-1 text-left">
            {t('settings.language')}: {currentLanguage === 'de-DE' ? 'DE' : 'EN'}
          </span>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" side="top">
        <DropdownMenuItem
          onClick={() => handleLanguageChange('de-DE')}
          className={currentLanguage === 'de-DE' ? 'bg-accent' : ''}
        >
          <div className="flex items-center gap-2">
            <span className="font-bold mr-1">DE</span> {t('settings.german')}
          </div>
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => handleLanguageChange('en-US')}
          className={currentLanguage === 'en-US' ? 'bg-accent' : ''}
        >
          <div className="flex items-center gap-2">
            <span className="font-bold mr-1">EN</span> {t('settings.english')}
          </div>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
